import axios from "axios";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "../stores/authStore";
import { useTenantStore } from "../stores/tenantStore";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  withCredentials: true
});

let refreshPromise: Promise<string | null> | null = null;

apiClient.interceptors.request.use((config) => {
  const auth = useAuthStore.getState();
  const tenant = useTenantStore.getState();
  if (auth.accessToken) config.headers.Authorization = `Bearer ${auth.accessToken}`;
  if (tenant.orgId) config.headers["X-Org-Id"] = tenant.orgId;
  if (tenant.financialYear) config.headers["X-FY"] = tenant.financialYear;
  if (tenant.financialYearId) config.headers["X-FY-Id"] = tenant.financialYearId;
  if (import.meta.env.DEV) {
    config.headers["Cache-Control"] = "no-store";
    config.headers.Pragma = "no-cache";
    if ((config.method ?? "get").toLowerCase() === "get") {
      config.params = { ...(config.params ?? {}), _devNoCache: Date.now() };
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (error.response?.status === 403) return Promise.reject(error);
    if (!original || original._retry || error.response?.status !== 401) return Promise.reject(error);
    original._retry = true;
    refreshPromise ??= refreshAccessToken();
    const token = await refreshPromise;
    refreshPromise = null;
    if (!token) {
      useAuthStore.getState().clearSession();
      return Promise.reject(error);
    }
    original.headers.Authorization = `Bearer ${token}`;
    return apiClient(original);
  }
);

async function refreshAccessToken() {
  const refreshToken = useAuthStore.getState().refreshToken;
  try {
    if (!refreshToken) return null;
    const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken }, { withCredentials: true });
    const token = data?.data?.accessToken ?? data?.accessToken ?? data?.token;
    const nextRefresh = data?.data?.refreshToken ?? data?.refreshToken ?? refreshToken;
    if (token) {
      useAuthStore.getState().setSession({ accessToken: token, refreshToken: nextRefresh, user: useAuthStore.getState().user });
      return token;
    }
  } catch {
    return null;
  }
  return null;
}
